import { useEffect, useState } from "react";
import { Loader2, Play, RotateCw } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { OFFLINE_TEXT } from "@/components/layout/OfflineBanner";

const Unavailable = ({ onRetry }) => (
  <div className="lb-unavailable" data-testid="lightbox-unavailable">
    <p className="max-w-md px-6">{OFFLINE_TEXT}</p>
    <button className="lb-retry" onClick={onRetry} data-testid="lightbox-retry-button"><RotateCw size={14} /> Try again</button>
  </div>
);

// Static stand-in when a video can't start (storage slow/offline): poster or gradient + play icon; retries in the background.
const VideoPlaceholder = ({ it, onRetry }) => (
  <div className="video-placeholder lb-video-placeholder" style={it.full_url ? { backgroundImage: `url(${mediaUrl(it.full_url)})` } : undefined} data-testid="lightbox-video-placeholder">
    <span className="vp-icon"><Play size={30} /></span>
    <p>This video isn't ready to play yet. Retrying automatically…</p>
    <button className="lb-retry" onClick={onRetry} data-testid="lightbox-video-retry"><RotateCw size={14} /> Try now</button>
  </div>
);

const STALL_MS = 15000;
const RETRY_MS = 20000;

export const usePrefetch = (items, index) => {
  useEffect(() => {
    [items[index + 1], items[index - 1]].forEach((n) => {
      if (n && n.media_type === "photo" && n.full_url) new Image().src = mediaUrl(n.full_url);
    });
  }, [items, index]);
};

export const LightboxMedia = ({ it }) => {
  const [state, setState] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const video = it.media_type === "video";
  useEffect(() => { setState("loading"); setAttempt(0); }, [it.id]);
  useEffect(() => {  // a video that never starts falls back to the placeholder instead of spinning forever
    if (!video || state !== "loading") return undefined;
    const t = setTimeout(() => setState("stalled"), STALL_MS);
    return () => clearTimeout(t);
  }, [video, state, attempt]);
  useEffect(() => {
    if (state !== "stalled" || attempt >= 6) return undefined;
    const t = setTimeout(() => { setState("loading"); setAttempt((a) => a + 1); }, RETRY_MS);
    return () => clearTimeout(t);
  }, [state, attempt]);
  const bust = attempt ? `${video ? it.video_url : it.full_url}${(it.full_url || "").includes("?") ? "&" : "?"}r=${attempt}` : null;
  const retry = () => { setState("loading"); setAttempt((a) => a + 1); };
  if (state === "error") return <Unavailable onRetry={retry} />;
  if (state === "stalled") return <VideoPlaceholder it={it} onRetry={retry} />;
  return (
    <>
      {state === "loading" && <Loader2 className="lb-spinner animate-spin" size={28} data-testid="lightbox-loading" />}
      {video ? (
        <video key={`${it.id}-${attempt}`} src={mediaUrl(bust || it.video_url)} poster={mediaUrl(it.full_url)} controls autoPlay playsInline
          preload="metadata" className="lightbox-media" data-testid="lightbox-video"
          onLoadedMetadata={() => setState("ok")} onError={() => setState("stalled")} />
      ) : (
        <img key={`${it.id}-${attempt}`} src={mediaUrl(bust || it.full_url || it.thumb_url)} alt="" className="lightbox-media" data-testid="lightbox-image"
          style={{ backgroundImage: `url(${mediaUrl(it.thumb_url)})` }}
          onLoad={() => setState("ok")} onError={() => setState("error")} />
      )}
    </>
  );
};
